/* eslint-disable react/jsx-props-no-spreading */
import {
    Dispatch,
    ReactElement,
    SetStateAction,
    useContext,
    useEffect,
} from "react"
import { FormProvider, UseFormReturn } from "react-hook-form"

import {
    METER_NUMBER_KW,
    METER_NUMBER_LABEL,
} from "../../../../../constants/generalConstants"
import { useGetUserData } from "../../../../../hooks/useGetUserData"
import useSubmitDraft from "../../../../../services/middleware/hooks/draftApplication/useSubmitDraft"
import { IFormData } from "../../../../../services/middleware/types/amendApplication/launchAmendmentTypes"
import { IFormProperties } from "../../../../../services/middleware/types/singleApplication/getSchemaTypes"
import { getValue } from "../../../../../utils/general/generalUtils"
import {
    prepareFormProperties,
    processUserAccountDerivedInputs,
    scrollToTop,
} from "../../../../../utils/submitApplication/submitApplicationUtils"
import FormErrors from "../../../../formErrors/formErrors"
import { ApplicationContext } from "../../applicationProvider"
import InputGenerator from "../../inputGenerator/inputGenerator"
import {
    DERIVED_INPUT,
    FIXED_INPUT,
    USER_ACCOUNT_DERIVED_INPUT,
} from "../../recognisedInputs/recognisedInputKeys"
import BackNextButtons from "../backNextButtons"
import { useFormGenerator } from "../useFormGenerator"

interface IBasicFormGeneratorProps {
    formKey: string
    formProperties: IFormProperties
    formData: IFormData
    isFirstStep: boolean
    setFormData: Dispatch<SetStateAction<IFormData>>
    setCurrentStepIndex: Dispatch<SetStateAction<number>>
    onClickNextSideEffect?: (methods: UseFormReturn) => void
    onClickBackFirstStep?: () => void
    firstStepNameOverride?: string
    isAmendment: boolean
}

/**
 * Dynamically renders a form using an input schema. The form is uncontrolled
 * and uses react-hook-form to validate the inputs. Form output is "watched"
 * by react hook form, producing a JSON object of with keys aligned to to
 * form spec (API) input naming convention. This component is used to render
 * base forms dynamically. It does not render recognised forms.
 *
 * This component is context agnostic and should be used in a context-aware
 * component.
 *
 *
 *
 */
const BaseFormGenerator = ({
    formKey,
    formProperties,
    formData,
    isFirstStep,
    setFormData,
    setCurrentStepIndex,
    onClickNextSideEffect,
    onClickBackFirstStep,
    isAmendment,
    firstStepNameOverride = undefined,
}: IBasicFormGeneratorProps): ReactElement => {
    const { currentStepIndex } = useContext(ApplicationContext)
    const { email, firstName, lastName } = useGetUserData()
    const { postDraftApplication } = useSubmitDraft()

    const initialValues = formData?.[formKey] as {
        [x: string]: string | number | boolean | undefined
    }

    const { errors, ...methods } =
        useFormGenerator<typeof initialValues>(initialValues)

    // Process form values similar to onClickNext but for real-time updates
    const processFormValues = (
        formWatch: Record<string, any>
    ): { [x: string]: unknown | undefined } =>
        Object.entries(formProperties).reduce<{
            [x: string]: unknown | undefined
        }>((acc, [key, value]) => {
            const inputType = value.json_schema_extra.type
            if (inputType === FIXED_INPUT) {
                return {
                    ...acc,
                    [key]: value.json_schema_extra.fixedValue ?? "",
                }
            }
            if (inputType === USER_ACCOUNT_DERIVED_INPUT) {
                return processUserAccountDerivedInputs(
                    acc,
                    key,
                    value,
                    email,
                    firstName,
                    lastName
                )
            }
            if (inputType === DERIVED_INPUT) {
                const sourceValue =
                    formWatch[
                        value.json_schema_extra.derivedValue?.source ?? ""
                    ] ?? ""
                return {
                    ...acc,
                    [key]:
                        value.json_schema_extra.derivedValue
                            ?.sourceOutputMappings[sourceValue as string] ??
                        null,
                }
            }
            return acc
        }, formWatch)

    // Watch for changes in the form
    useEffect(() => {
        const subscription = methods.watch((formValues) => {
            // Only process if we have actual values
            if (formValues && Object.keys(formValues).length > 0) {
                // Create a deep copy of the current values to avoid reference issues
                const formWatch = JSON.parse(JSON.stringify(formValues))
                const processedValues = processFormValues(formWatch)

                // Update the form data context - always preserve installerCustomerDetails
                // Get current form values for all fields
                const currentFormData = {
                    ...formData,
                    [formKey]: processedValues,
                }

                // Preserve installer customer details if they exist
                if (formData.installerCustomerDetails) {
                    currentFormData.installerCustomerDetails =
                        formData.installerCustomerDetails
                }

                setFormData(currentFormData)
            }
        })

        return () => {
            subscription.unsubscribe()
        }
    }, [methods.watch])

    const onClickNext = async (): Promise<void> => {
        // Required so to avoid async re-rendering issues
        const formWatch = JSON.parse(JSON.stringify(methods.getValues()))
        const processedValues = processFormValues(formWatch)

        // Create updated form data
        const updatedFormData = {
            ...formData,
            [formKey]: processedValues,
        }

        // Explicitly preserve installerCustomerDetails
        if (
            formData.installerCustomerDetails &&
            formKey !== "installerCustomerDetails"
        ) {
            updatedFormData.installerCustomerDetails =
                formData.installerCustomerDetails
        }

        // Update form data
        setFormData(updatedFormData)

        scrollToTop(document)
        setCurrentStepIndex((prev) => prev + 1)

        // Save draft with the latest data if we have installer customer details
        if (updatedFormData.installerCustomerDetails) {
            postDraftApplication(false, updatedFormData, currentStepIndex + 1)
        }

        // Call side effect if provided
        if (onClickNextSideEffect) {
            onClickNextSideEffect(methods)
        }
    }

    return (
        <FormProvider {...methods}>
            {prepareFormProperties(formProperties).map(([key, value]) => (
                <InputGenerator<string | number | boolean | undefined>
                    key={key}
                    isAmendment={isAmendment}
                    registerName={key}
                    inputType={value.json_schema_extra.type}
                    inputSpec={value}
                    watch={(k) => methods.watch(k)}
                    programmaticallySetForm={(k, v) => methods.setValue(k, v)}
                />
            ))}

            <FormErrors
                errors={errors}
                getFriendlyName={(key: string) =>
                    getValue(
                        {
                            ...formProperties,
                            [METER_NUMBER_KW]: { title: METER_NUMBER_LABEL },
                        },
                        key
                    )?.title ?? key
                }
            />
            <BackNextButtons
                onClickNext={() => {
                    methods.handleSubmit(onClickNext)()
                }}
                setCurrentStepIndex={setCurrentStepIndex}
                isFirstStep={isFirstStep}
                onClickBackFirstStep={onClickBackFirstStep}
                firstStepNameOverride={firstStepNameOverride}
            />
        </FormProvider>
    )
}

BaseFormGenerator.defaultProps = {
    onClickNextSideEffect: undefined,
    onClickBackFirstStep: undefined,
    firstStepNameOverride: undefined,
}

export default BaseFormGenerator
